/**
 * Client-side helper for reading the JS-readable presence flag cookie
 * the server sets in lockstep with the HttpOnly auth JWT.
 *
 * The flag is a render hint — it tells client code "this browser
 * has *some* duudey.com session" without paying for a network probe.
 * Anything authoritative still verifies the real JWT server-side.
 *
 * Two callers today:
 *   - ``site-shell.tsx`` skips the ``/api/auth/me`` probe for guests.
 *   - ``thatradiothing-client.tsx`` chooses between the authenticated
 *     ``/status`` route and the public ``/api/now_playing`` route on
 *     the radio dock without ever asking the server "are you logged
 *     in".
 *
 * Both clear the flag locally on a 401 from a route that should have
 * accepted their session — the server will re-mint the flag on the
 * next probe if the JWT actually still validates. This keeps the
 * polling cheap without coupling either caller to the other.
 */

// Must match ``LOGGED_IN_COOKIE_NAME`` in ``src/lib/auth/jwt.ts``.
// Hard-coding the same default here (rather than re-exporting the
// server module) keeps the client bundle free of any server-only
// imports.
export const LOGGED_IN_COOKIE_NAME = "duudey_logged_in";

/**
 * True when the flag cookie is present *and* non-empty. We accept any
 * truthy value (the server currently writes ``"1"``) — the existence
 * itself is the signal.
 */
export function hasLoggedInFlagCookie(): boolean {
  if (typeof document === "undefined") return false;
  const prefix = `${LOGGED_IN_COOKIE_NAME}=`;
  return document.cookie.split(";").some((entry) => {
    const trimmed = entry.trim();
    return trimmed.startsWith(prefix) && trimmed.length > prefix.length;
  });
}

/**
 * Locally evict the flag cookie. Used as the self-heal step when a
 * route that ought to have accepted the session returns 401 — the
 * server-side JWT cookie may have been rejected (signature rotation,
 * stale token), and we don't want subsequent polls in the same tab
 * to keep hitting authenticated endpoints. The next server response
 * that *does* validate will re-mint the flag automatically (see
 * ``setSessionCookies`` in ``src/lib/auth/jwt.ts``).
 *
 * Setting Max-Age=0 with the same cookie attributes the server used
 * is the cross-browser-safe way to delete a cookie from JS. Domain
 * defaults to the current host; the server-set cookie carries
 * ``Domain=.duudey.com``, but a host-only deletion still wins
 * because browsers also accept attribute-less Max-Age=0 directives
 * on a name match.
 */
export function clearLoggedInFlagCookieLocally(): void {
  if (typeof document === "undefined") return;
  document.cookie = `${LOGGED_IN_COOKIE_NAME}=; Path=/; Max-Age=0; SameSite=Lax`;
  // Also try the explicit subdomain-shared form. Browsers ignore the
  // one that doesn't match an existing cookie.
  document.cookie = `${LOGGED_IN_COOKIE_NAME}=; Path=/; Max-Age=0; SameSite=Lax; Domain=.duudey.com`;
}
